"use client";

import { c as _c } from "react/compiler-runtime";
import { type BackendProductsResponse, type CategoryEnum, EventEnum } from "@sprawdzonabron/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAnalyticsEvent } from "../../api/hooks/useAnalyticsEvent";
import { getProducts } from "../../api/services/getProducts";
import { type Filters } from "../../features/filters/types";
import { areFiltersDefault } from "../../features/filters/utils";
import { useDebounce } from "../../hooks/useDebounce";
import { selectFilters, useSelector } from "../../store/selectors";
import type { ErrorResponse } from "../../types/errors";
import { omitEmpty } from "../../utils/omitEmpty";
interface UseProductsProps {
  initialProducts: Awaited<ReturnType<typeof getProducts>>;
  defaultCategory?: CategoryEnum;
  defaultSearch?: string;
  defaultProducer?: string[];
  defaultCity?: string;
}
export const useProducts = t0 => {
  const $ = _c(30);
  const {
    initialProducts,
    defaultCategory,
    defaultSearch,
    defaultProducer,
    defaultCity
  } = t0;
  let t1;
  if ($[0] !== initialProducts.data) {
    t1 = initialProducts.data ?? {
      products: [],
      total: 0,
      hasMore: false,
      stats: {
        productsByShop: {},
        minPrice: 0,
        maxPrice: 0,
        isAvailable: false
      }
    };
    $[0] = initialProducts.data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [data, setData] = useState(t1);
  const [hasMore, setHasMore] = useState(initialProducts.data?.hasMore ?? false);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(initialProducts.error);
  const filters = useSelector(selectFilters);
  const debouncedFilters = useDebounce(filters, 500);
  let t2;
  if ($[2] !== defaultCategory || $[3] !== defaultCity || $[4] !== defaultProducer || $[5] !== defaultSearch) {
    t2 = omitEmpty({
      category: defaultCategory,
      search: defaultSearch,
      producer: defaultProducer,
      city: defaultCity
    });
    $[2] = defaultCategory;
    $[3] = defaultCity;
    $[4] = defaultProducer;
    $[5] = defaultSearch;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const defaultFilters = t2;
  let t3;
  bb0: {
    if (defaultCategory && debouncedFilters.category !== defaultCategory) {
      t3 = true;
      break bb0;
    }
    if (defaultCity && debouncedFilters.city !== defaultCity) {
      t3 = true;
      break bb0;
    }
    if (defaultSearch && debouncedFilters.search !== defaultSearch) {
      t3 = true;
      break bb0;
    }
    if (defaultProducer) {
      const producer1 = [...(debouncedFilters.producer || [])].sort().join(",");
      const producer2 = [...defaultProducer].sort().join(",");
      if (producer1 !== producer2) {
        t3 = true;
        break bb0;
      }
    }
    let t4;
    if ($[7] !== debouncedFilters || $[8] !== defaultFilters) {
      t4 = areFiltersDefault(debouncedFilters, defaultFilters);
      $[7] = debouncedFilters;
      $[8] = defaultFilters;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    t3 = t4;
  }
  const isDefault = t3;
  let t4;
  if ($[10] !== debouncedFilters || $[11] !== initialProducts.data || $[12] !== initialProducts.error || $[13] !== isDefault) {
    t4 = () => {
      if (isDefault) {
        if (initialProducts.data) {
          setData(initialProducts.data);
          setHasMore(initialProducts.data.hasMore);
        }
        setPage(1);
        setError(initialProducts.error);
        setIsLoading(false);
      } else {
        (async () => {
          setIsLoading(true);
          setError(null);
          const response = await getProducts(debouncedFilters, 1);
          if (response.error) {
            setError(response.error);
            setIsLoading(false);
            return;
          }
          setData(response.data);
          setHasMore(response.data.hasMore);
          setPage(1);
          setIsLoading(false);
        })();
      }
    };
    $[10] = debouncedFilters;
    $[11] = initialProducts.data;
    $[12] = initialProducts.error;
    $[13] = isDefault;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== debouncedFilters || $[16] !== initialProducts || $[17] !== isDefault) {
    t5 = [debouncedFilters, isDefault, initialProducts];
    $[15] = debouncedFilters;
    $[16] = initialProducts;
    $[17] = isDefault;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  useEffect(t4, t5);
  const postFetchMoreEvent = useAnalyticsEvent(EventEnum.FetchMore);
  let t6;
  if ($[19] !== filters || $[20] !== isLoading || $[21] !== page || $[22] !== postFetchMoreEvent) {
    t6 = async () => {
      if (isLoading) {
        return;
      }
      setIsLoading(true);
      setError(null);
      const nextPage = page + 1;
      postFetchMoreEvent(`page: ${nextPage}`);
      const response_0 = await getProducts(filters, nextPage);
      if (response_0.error) {
        setError(response_0.error);
        setIsLoading(false);
        return;
      }
      const newData = response_0.data;
      if (newData.products.length === 0 || !newData.hasMore) {
        setHasMore(false);
      }
      setData(prev => ({
        ...prev,
        products: [...prev.products, ...newData.products]
      }));
      setPage(nextPage);
      setIsLoading(false);
    };
    $[19] = filters;
    $[20] = isLoading;
    $[21] = page;
    $[22] = postFetchMoreEvent;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const loadMore = t6;
  let t7;
  if ($[24] !== data || $[25] !== error || $[26] !== hasMore || $[27] !== isLoading || $[28] !== loadMore) {
    t7 = {
      data,
      hasMore,
      isLoading,
      error,
      loadMore
    };
    $[24] = data;
    $[25] = error;
    $[26] = hasMore;
    $[27] = isLoading;
    $[28] = loadMore;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  return t7;
};